import { useQuery } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

export interface DuplicateMatch {
  file_name: string;
  uploaded_at: string;
  purchase_order_id: string;
}

export async function fetchDuplicateMatches(
  client: SupabaseClient,
  organisationId: string,
  hashes: string[],
): Promise<Record<string, DuplicateMatch>> {
  if (hashes.length === 0) return {};

  const { data, error } = await client
    .from('purchase_order_files')
    .select('id, file_name, uploaded_at, file_hash, purchase_orders!inner(id, status, organisation_id)')
    .in('file_hash', hashes)
    .eq('purchase_orders.organisation_id', organisationId)
    .neq('purchase_orders.status', 'archived');

  if (error) {
    console.error('[useDuplicateFileCheck] query failed:', error);
    return {};
  }

  const result: Record<string, DuplicateMatch> = {};
  for (const row of data ?? []) {
    const hash = row.file_hash as string | null;
    if (!hash || result[hash]) continue;
    const po = row.purchase_orders as { id: string };
    result[hash] = {
      file_name: row.file_name as string,
      uploaded_at: row.uploaded_at as string,
      purchase_order_id: po.id,
    };
  }
  return result;
}

export function useDuplicateFileCheck(
  organisationId: string,
  fileHashes: Record<string, string>,
): Map<string, DuplicateMatch | null> {
  const hashes = Object.values(fileHashes);

  const { data } = useQuery({
    queryKey: ['duplicate-file-check', organisationId, ...hashes.sort()],
    queryFn: () => fetchDuplicateMatches(supabase, organisationId, hashes),
    enabled: !!organisationId && hashes.length > 0,
  });

  const result = new Map<string, DuplicateMatch | null>();
  for (const [fileId, hash] of Object.entries(fileHashes)) {
    result.set(fileId, data?.[hash] ?? null);
  }
  return result;
}
